import React, { useContext } from "react";

import { css, useTheme } from "@emotion/react";

import useAddressApi from "../../../../../../hooks/useAddressApi";
import { DataContext } from "../../../../../../providers/PelletsCheckoutDataProvider";
import AddressFallback from "../../../../../shared/AddressFallback";
import Columns from "../../../../../shared/Columns";
import Field from "../../../../../shared/forms/Field";
import FormError from "../../../../../shared/forms/FormError";
import NewInput from "../../../../../shared/forms/NewInput";
import SelectInput from "../../../../../shared/forms/NewSelect";
import Headline from "../../../../../shared/NewHeadline";
import { ZipcodeCityWrapper } from "../../../../../shared/orders/StyledComponents";
import TextAlign from "../../../../../shared/TextAlign";

const DeliveryAddress = () => {
    const theme = useTheme();
    const dataContext = useContext(DataContext);

    const houseNumberStyles = css`
        margin-top: -10px;
        ${theme.breakpoints.from.small.css`
            margin-top: 0;
        `}
    `;

    const { deliveryAddressData, up, amountFirstUnloadingPoint } =
        dataContext.data;

    const {
        setDeliveryAddressData,
        addFormError,
        clearFormError,
        hasFormErrorAt,
        changeSelectedStreet,
        changeSelectedCity,
        onGooglePlacesChange,
    } = dataContext.actions;

    const { cities, streets, addressApiError } = useAddressApi({
        zipcode: deliveryAddressData.zipcode,
        city: deliveryAddressData.city,
    });

    const handleHouseNumberChange = (event) => {
        setDeliveryAddressData({
            ...deliveryAddressData,
            house_number: event.target.value,
        });
    };

    const handleCityChange = (event) => {
        changeSelectedStreet("");
        changeSelectedCity(event.target.value);
    };

    const handleStreetChange = (event) => {
        changeSelectedStreet(event.target.value);
    };

    return (
        <>
            {up === 1 ? (
                <Headline>Lieferadresse</Headline>
            ) : (
                <div className="columns is-mobile">
                    <div className="column">
                        <Headline>Haushalt 1</Headline>
                    </div>
                    <TextAlign
                        align="right"
                        className="column"
                        css={css`
                            padding-top: 20px;
                        `}
                    >
                        {amountFirstUnloadingPoint}&nbsp;l
                    </TextAlign>
                </div>
            )}
            {!addressApiError ? (
                <>
                    <Columns>
                        <Columns.Column tablet="one-fifth">
                            <Field>
                                <ZipcodeCityWrapper>
                                    <ZipcodeCityWrapper.Label>
                                        {deliveryAddressData.zipcode}
                                    </ZipcodeCityWrapper.Label>
                                </ZipcodeCityWrapper>
                            </Field>
                        </Columns.Column>
                        <Columns.Column>
                            <SelectInput
                                name="city"
                                placeholder="Bitte wähle Deinen Ort aus"
                                withDefault
                                options={cities.map((city) => ({
                                    value: city.ONAME,
                                    text: city.ONAME,
                                }))}
                                value={
                                    cities.find((city) => {
                                        return (
                                            city.ONAME ===
                                            deliveryAddressData.city
                                        );
                                    })?.ONAME ?? ""
                                }
                                onChange={handleCityChange}
                            />
                            {hasFormErrorAt("city") && (
                                <FormError
                                    message={"Bitte wähle einen Ort aus."}
                                />
                            )}
                        </Columns.Column>
                    </Columns>

                    <Columns>
                        <Columns.Column
                            mobile="one"
                            tablet="two-thirds"
                            desktop="two-thirds"
                        >
                            <SelectInput
                                name="street"
                                placeholder="Bitte wähle Deine Straße aus"
                                withDefault
                                options={streets.map((street) => {
                                    return {
                                        value: street,
                                        text: street,
                                    };
                                })}
                                value={
                                    streets.find(
                                        (street) =>
                                            street ===
                                            deliveryAddressData.street
                                    ) ?? ""
                                }
                                onChange={handleStreetChange}
                            />
                            {hasFormErrorAt("street") && (
                                <FormError
                                    message={"Bitte wähle eine Straße aus."}
                                />
                            )}
                        </Columns.Column>

                        <Columns.Column css={houseNumberStyles}>
                            <NewInput
                                placeholder="Haus.-Nr."
                                id="house_number"
                                name="house_number"
                                onChange={handleHouseNumberChange}
                                value={deliveryAddressData.house_number}
                                validation="house_number"
                                forceError={hasFormErrorAt("house_number")}
                                onError={() => addFormError("house_number")}
                                onErrorSolved={() =>
                                    clearFormError("house_number")
                                }
                            />
                        </Columns.Column>
                    </Columns>
                </>
            ) : (
                <AddressFallback
                    onGooglePlacesChange={onGooglePlacesChange}
                    zipcode={deliveryAddressData.zipcode}
                    handleHouseNumberChange={handleHouseNumberChange}
                    houseNumber={deliveryAddressData.house_number}
                    houseNumberHasErrorAt={hasFormErrorAt(
                        "deliveryAddress.house_number"
                    )}
                    houseNumberAddError={() =>
                        addFormError("deliveryAddress.house_number")
                    }
                    houseNumberClearError={() =>
                        clearFormError("deliveryAddress.house_number")
                    }
                />
            )}
        </>
    );
};

export default DeliveryAddress;
